/**
 * Folders API Service
 * Provides methods for managing folders (used by both Assets and Templates).
 */

import { api, handleApiError } from '../client'

// ============================================
// Types
// ============================================

export interface FolderDto {
  id: string
  name: string
  description?: string
  parentId?: string
  color?: string
  assetCount: number
  templateCount: number
  childCount: number
  createdAt: string
  updatedAt: string
}

export interface FolderHierarchyDto extends FolderDto {
  children: FolderDto[]
  breadcrumbs: FolderBreadcrumb[]
}

export interface FolderBreadcrumb {
  id: string
  name: string
}

export interface FolderTreeNode {
  id: string
  name: string
  description?: string
  color?: string
  assetCount: number
  templateCount: number
  children: FolderTreeNode[]
}

export interface PaginatedFolderResponse {
  items: FolderDto[]
  pageNumber: number
  totalPages: number
  totalCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export interface CreateFolderRequest {
  name: string
  description?: string
  parentId?: string
  color?: string
}

export interface UpdateFolderRequest {
  name?: string
  description?: string
  color?: string
}

// ============================================
// Folders Service
// ============================================

export const foldersService = {
  /**
   * Gets folders with pagination and filtering.
   */
  async getFolders(options?: {
    parentId?: string | null
    searchTerm?: string
    sortBy?: string
    sortDescending?: boolean
    pageNumber?: number
    pageSize?: number
  }): Promise<PaginatedFolderResponse> {
    try {
      const params = new URLSearchParams()

      if (options?.parentId !== undefined) {
        if (options.parentId === null) {
          // Root folders
          params.set('parentId', '')
        } else {
          params.set('parentId', options.parentId)
        }
      }
      if (options?.searchTerm) {
        params.set('searchTerm', options.searchTerm)
      }
      if (options?.sortBy) {
        params.set('sortBy', options.sortBy)
      }
      if (options?.sortDescending !== undefined) {
        params.set('sortDescending', String(options.sortDescending))
      }
      if (options?.pageNumber) {
        params.set('pageNumber', String(options.pageNumber))
      }
      if (options?.pageSize) {
        params.set('pageSize', String(options.pageSize))
      }

      const queryString = params.toString()
      const response = await api.get<PaginatedFolderResponse>(
        `/v1/folders${queryString ? `?${queryString}` : ''}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets root folders (folders without a parent).
   */
  async getRootFolders(): Promise<FolderDto[]> {
    try {
      const response = await this.getFolders({ parentId: null })
      return response.items
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a folder by ID with hierarchy information.
   */
  async getFolder(id: string, includeChildren = true): Promise<FolderHierarchyDto> {
    try {
      const response = await api.get<FolderHierarchyDto>(
        `/v1/folders/${id}?includeChildren=${includeChildren}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the folder tree structure.
   */
  async getFolderTree(rootId?: string, maxDepth?: number): Promise<FolderTreeNode[]> {
    try {
      const params = new URLSearchParams()
      if (rootId) {
        params.set('rootId', rootId)
      }
      if (maxDepth !== undefined) {
        params.set('maxDepth', String(maxDepth))
      }

      const queryString = params.toString()
      const response = await api.get<FolderTreeNode[]>(
        `/v1/folders/tree${queryString ? `?${queryString}` : ''}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new folder.
   */
  async createFolder(data: CreateFolderRequest): Promise<FolderDto> {
    try {
      const response = await api.post<FolderDto>('/v1/folders', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing folder.
   */
  async updateFolder(id: string, data: UpdateFolderRequest): Promise<FolderDto> {
    try {
      const response = await api.put<FolderDto>(`/v1/folders/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a folder.
   */
  async deleteFolder(id: string, recursive = false): Promise<void> {
    try {
      await api.delete(`/v1/folders/${id}?recursive=${recursive}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Moves a folder to a new parent.
   */
  async moveFolder(id: string, newParentId: string): Promise<FolderDto> {
    try {
      const response = await api.post<FolderDto>(`/v1/folders/${id}/move/${newParentId}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Moves a folder to root level.
   */
  async moveFolderToRoot(id: string): Promise<FolderDto> {
    try {
      const response = await api.post<FolderDto>(`/v1/folders/${id}/move-to-root`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
